import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../utils";
import { Button } from "../button";
import { Slugger } from "../slug";
import { highlight, normalizeLang } from "./syntax";

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Flatten a hast node to its text content (for slugging). */
function hastText(node: any): string {
  if (!node) return "";
  if (node.type === "text") return typeof node.value === "string" ? node.value : "";
  if (Array.isArray(node.children)) return node.children.map(hastText).join("");
  return "";
}

/**
 * A tiny `rehype-slug`: stamp deterministic, de-duplicated `id`s on TOP-LEVEL
 * h2/h3 in the REHYPE phase — a pure single pass over the hast tree, NOT React
 * render. Doing it here (rather than in the heading component) is what makes it
 * hydration-safe: a render-phase `Slugger` gets double-invoked by React's dev
 * StrictMode and mis-counts duplicates (server `#x` vs client `#x-1`). It runs
 * once per parse over the SAME h2/h3 set as `deriveToc` (top-level headings, in
 * order), so the on-this-page anchors and the rendered ids agree.
 *
 * Only DIRECT children of the root are slugged — a heading nested inside a
 * blockquote/list (`> ## x`) is not a section heading, gets no id, and stays out
 * of the TOC (matching `deriveToc`'s line-start match, so the two never diverge).
 */
function rehypeHeadingIds() {
  return (tree: any) => {
    const slugger = new Slugger();
    const children = Array.isArray(tree?.children) ? tree.children : [];
    for (const node of children) {
      if (
        node?.type === "element" &&
        (node.tagName === "h2" || node.tagName === "h3")
      ) {
        node.properties = node.properties ?? {};
        if (node.properties.id == null) {
          node.properties.id = slugger.slug(hastText(node));
        }
      }
    }
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * MarkdownProse renders a single block of GitHub-flavored markdown with
 * documentation-grade typography. It is the single rendering path for all
 * auto-generated paragraph/list/callout/table/code content and for user
 * notes; that keeps inline-formatting (bold, italics, inline code, links)
 * consistent everywhere.
 *
 * Body copy is `type-body`; headings step up the five-role scale (H1/H2 type-heading,
 * H3 type-title, H4 type-body, all Medium) so structure reads at a glance — in
 * lockstep with the dedicated `Heading` component. Top-level section headings
 * carry the copy-link affordance via that `Heading` component; MarkdownProse
 * only ever sees the nested headings inside user notes.
 */
export function MarkdownProse({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div className={cn("doc-prose type-body text-foreground", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHeadingIds]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-8 mb-3 type-heading text-foreground first:mt-0">
              {children}
            </h1>
          ),
          // `id` is stamped in the rehype phase (rehypeHeadingIds) so it's
          // deterministic across SSR/hydration; we just forward it here.
          h2: ({ id, children }) => (
            <h2
              id={id}
              className="mt-6 mb-2 type-heading font-medium text-foreground first:mt-0"
            >
              {children}
            </h2>
          ),
          h3: ({ id, children }) => (
            <h3
              id={id}
              className="mt-4 mb-1.5 type-title font-medium text-foreground first:mt-0"
            >
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="mt-3 mb-1 type-title font-medium text-muted-foreground first:mt-0">
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="my-3 leading-body type-body text-foreground first:mt-0 last:mb-0">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="my-3 list-disc space-y-1.5 pl-5 marker:text-muted-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-3 list-decimal space-y-1.5 pl-5 marker:text-muted-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="type-body leading-body text-foreground [&>p]:my-0">
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-4 border-l-2 border-border bg-muted/30 px-4 py-2 type-body text-muted-foreground [&>p]:my-1">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith("http") ? "_blank" : undefined}
              rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
              className="type-body text-foreground underline decoration-border underline-offset-2 transition-colors hover:decoration-foreground"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="text-foreground">{children}</strong>, // design-audit-ignore html-strong-bold: markdown renderer must emit semantic <strong>
          em: ({ children }) => (
            <em className="italic text-foreground">{children}</em>
          ),
          hr: () => <hr className="my-8 border-border/60" />,
          // Tables mirror the app's canonical Table atoms (packages/ui/src/
          // table.tsx): a Card-like bordered surface, `bg-surface-3` non-caps
          // muted header (Regular weight, no `font-medium`), `p-2` cells, and
          // required column dividers (`border-r border-border/40
          // last:border-r-0`). Works for arbitrary auto-generated markdown
          // tables with variable columns.
          table: ({ children }) => (
            <div className="my-5 overflow-x-auto rounded-md border border-border bg-card">
              <table className="w-full caption-bottom border-collapse type-body">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-surface-3">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border/40 last:border-b-0">
              {children}
            </tr>
          ),
          th: ({ children, style }) => (
            <th
              style={style}
              className="border-r border-border/40 p-2 text-left align-middle text-muted-foreground last:border-r-0"
            >
              {children}
            </th>
          ),
          td: ({ children, style }) => (
            <td
              style={style}
              className="border-r border-border/40 p-2 align-top text-foreground last:border-r-0"
            >
              {children}
            </td>
          ),
          code: ({ className, children }) => {
            const isBlock = /language-/.test(className ?? "");
            if (isBlock) {
              return (
                <code
                  className={cn(
                    "font-mono type-body leading-body text-foreground",
                    className,
                  )}
                >
                  {children}
                </code>
              );
            }
            return (
              <code className="rounded bg-muted/60 px-1 py-px font-mono type-body text-foreground">
                {children}
              </code>
            );
          },
          pre: ({ children }) => <FencedCode>{children}</FencedCode>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Fenced code blocks rendered inside MarkdownProse get a language pill in
 * the header plus a copy button that reveals on hover. We accept the
 * raw <pre>{children}</pre> shape react-markdown gives us and reach into
 * its single <code> child to pull out the language class + raw text.
 */
function FencedCode({ children }: { children: ReactNode }) {
  const { language, text } = extractFromPre(children);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard may be unavailable */
    }
  }

  // Tokenize server-side (this component is a client component but the
  // tokenizer runs synchronously and emits HTML once). Unknown languages
  // pass through escaped — no spans, just plain mono.
  const lang = normalizeLang(language);
  const html = highlight(text, lang);

  return (
    <div className="group my-4 overflow-hidden rounded-md border border-border bg-muted/40">
      <div className="flex items-center justify-between border-b border-border/60 bg-muted/60 px-3 py-1">
        <span className="font-mono type-subtext uppercase tracking-wide text-muted-foreground">
          {language ?? "code"}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={copy}
          className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 type-body leading-body text-foreground">
        <code
          className="font-mono"
          // The tokenizer's output is fully HTML-escaped; only its own
          // `<span class="tok-*">` wrappers are unescaped. Safe to set.
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </pre>
    </div>
  );
}

function extractFromPre(children: ReactNode): {
  language: string | null;
  text: string;
} {
  let language: string | null = null;
  let text = "";

  function walk(node: ReactNode): void {
    if (node == null || node === false) return;
    if (typeof node === "string") {
      text += node;
      return;
    }
    if (typeof node === "number") {
      text += String(node);
      return;
    }
    if (Array.isArray(node)) {
      for (const child of node) walk(child);
      return;
    }
    if (typeof node === "object" && "props" in node) {
      const props = (node as { props: { className?: string; children?: ReactNode } }).props;
      if (!language && props.className) {
        const m = /language-([\w-]+)/.exec(props.className);
        if (m && m[1]) language = m[1];
      }
      walk(props.children);
    }
  }
  walk(children);
  return { language, text: text.replace(/\n$/, "") };
}
